import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useInterview } from '../../utils/interviewContext';
import { trackPostHogEvent } from '../../utils/posthogUtils';
import { getInterviewFlow, nextStep, previousStep, stepPosition } from '../../utils/interviewFlow';
import styles from './interviewQuestion.module.css';
import { Collapse } from '@mui/material';
import NotInterestedIcon from '@mui/icons-material/NotInterested';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import LocalDiningIcon from '@mui/icons-material/LocalDining';
import ScheduleIcon from '@mui/icons-material/Schedule';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import ProgressBar from '../.shared/ProgressBar/ProgressBar';
import Footer from '../.shared/footer/footer';
import Header from '../.shared/header/header';

const PATH = '/interview/diet';

// 'none' is the not-yet-answered sentinel, so the "no diet" answer is keyed
// no_diet — otherwise answering it would read as no answer at all.
const dietOptions = [
    { value: 'no_diet', label: 'No, not right now', Icon: NotInterestedIcon },
    { value: 'calorie_counting', label: 'Counting calories or watching portions', Icon: RestaurantIcon },
    { value: 'low_carb', label: 'Low-carb or keto', Icon: LocalDiningIcon },
    { value: 'fasting', label: 'Intermittent fasting', Icon: ScheduleIcon },
    { value: 'other', label: 'Something else', Icon: MoreHorizIcon }
];

export default function InterviewDiet() {
    const navigate = useNavigate();

    const { updateInterviewData, interviewData } = useInterview();
    const [ selectedDiet, setSelectedDiet ] = useState('none');

    // Read once per mount, so the chrome and the navigation cannot disagree if
    // the stored assignment changes while the page is open.
    const flow = useMemo(getInterviewFlow, []);

    // Populate from localStorage on component mount
    useEffect(() => {
        if (interviewData.currentDiet) {
            setSelectedDiet(interviewData.currentDiet);
        }

        // Track when user reaches the diet step
        trackPostHogEvent('interview_step_viewed', {
            interview_step: 'diet',
            step_number: stepPosition(flow, PATH),
            total_steps: flow.steps.length,
            interview_flow: flow.id,
            has_existing_data: !!interviewData.currentDiet
        });
    }, [interviewData.currentDiet, flow]);

    const handleConfirm = (recentlyChosen) => {
        if (selectedDiet === 'none' && !recentlyChosen) return;

        const chosenDiet = recentlyChosen ? recentlyChosen : selectedDiet;

        setSelectedDiet(chosenDiet);

        // Timeout to allow button selection animation to complete
        setTimeout(() => {
            // Save to interview context
            updateInterviewData('currentDiet', chosenDiet);

            // Track diet selection
            trackPostHogEvent('interview_diet_selected', {
                selected_diet: chosenDiet,
                diet_label: dietOptions.find((option) => option.value === chosenDiet)?.label,
                interview_step: 'diet',
                step_number: stepPosition(flow, PATH),
                interview_flow: flow.id
            });

            navigate(nextStep(flow, PATH) ?? '/interview/movement');
        }, 300);
    };

    const handleBack = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        navigate(previousStep(flow, PATH) ?? '/');
    };

    return (
        <>
            <div className={styles.interviewPageContainer}>
                <Header hideNavigation={flow.hideNavigation} />
                <ProgressBar step={stepPosition(flow, PATH)} totalSteps={flow.steps.length} />
                <Collapse
                    in
                    appear
                    timeout={500}
                    unmountOnExit
                    mountOnEnter
                    orientation="vertical"
                >
                    <div className={styles.questionContent}>
                        <h1 className={styles.title}>Are you currently following any diet?</h1>
                        <div className={styles.buttonsContainer}>

                            {dietOptions.map(({ value, label, Icon }) => (
                                <button
                                    key={value}
                                    className={`${styles.interviewButton} ${selectedDiet === value ? styles.selected : ''}`}
                                    onClick={() => handleConfirm(value)}
                                >
                                    <Icon sx={{ color: `${selectedDiet === value ? '#0D79FE' : '#4E4E4EFD'}` }} />
                                    <span>{label}</span>
                                </button>
                            ))}

                            <div className={styles.confirmationButtons}>
                                <button
                                    className={styles.secondaryButton}
                                    onClick={handleBack}
                                >
                                    Back
                                </button>
                                <button
                                    className={`${styles.primaryButton} ${selectedDiet === 'none' ? styles.disabledButton : ''}`}
                                    onClick={() => handleConfirm()}
                                >
                                    Continue
                                </button>
                            </div>

                        </div>
                    </div>
                </Collapse>
            </div>
            <Footer />
        </>
    );
}
